import React, { useEffect, useRef, useState } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  ActionListItem,
  Avatar,
  Box,
  Button,
  RowsSkeletonLoader,
  Tooltip,
} from '@node-space/ui-components'
import { ListItemAnimation } from 'components/Animations/ListItemAnimation'
import { SkeletonListItemAnimation } from 'components/Animations/SkeletonListItemAnimation'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import {
  BankAccountPaymentInstrument,
  Contact,
  ContactInstrument,
  ContactPaymentInstruments,
} from 'types/contacts'
import { getIneligibleBankAccountMessage } from './utils'

interface InstrumentListProps {
  contacts: ContactPaymentInstruments[]
  isFetchingNextPage: boolean
  isInstruments?: boolean
  hasLoadedMore: boolean
  hasNextPage: boolean
  handleContactSelect: (contact: Contact, contactInstrument?: ContactInstrument) => void
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<ContactPaymentInstruments[], Error>>
  setContactIds?: React.Dispatch<React.SetStateAction<string[]>>
  contactFilter?: string[]
}

export const InstrumentList = ({
  contacts,
  isFetchingNextPage,
  hasLoadedMore,
  hasNextPage,
  handleContactSelect,
  setHasLoadedMore,
  fetchNextPage,
  setContactIds,
  contactFilter,
}: InstrumentListProps) => {
  const { t } = useTranslation()

  const listRef = useRef<HTMLUListElement | null>(null)
  const [contactList, setContactList] = useState([])

  useEffect(() => {
    if (isFetchingNextPage && hasLoadedMore) {
      setContactList(prevItems => [...prevItems, { loading: true }, { loading: true }])
    } else if (contacts) {
      setContactList(contacts)
    }
  }, [contacts, isFetchingNextPage, hasLoadedMore])

  useEffect(() => {
    if (listRef.current && hasLoadedMore) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [hasLoadedMore])

  const handleOnLoadMore = () => {
    fetchNextPage()
    setHasLoadedMore(true)
  }

  const handleOnShowAll = () => {
    setContactIds?.([])
  }

  const getSecondaryText = (contactPaymentInstrument: ContactPaymentInstruments, ineligibleBankAccountMessage?: string) => {
    const { paymentInstrument } = contactPaymentInstrument || {}
    const { accountNumber, bank } = (paymentInstrument || {}) as BankAccountPaymentInstrument
    const { identificationCode, swiftCode } = bank || {}
    

    if (!ineligibleBankAccountMessage) {
      return {
        BodyPre: accountNumber,
        BodyAvatar:
          <Avatar
              size="tiny"
              type="currency"
              name={paymentInstrument?.currency?.toLowerCase()}
          />,
        BodyPost: identificationCode || swiftCode
      }
    }

    return {
      BodyPre: ineligibleBankAccountMessage,
    }
  }

  return (
    <motion.ul ref={listRef} className="h-[calc(80vh-244px)] overflow-y-auto">
      <AnimatePresence>
        {contactList?.map((currentPaymentInstrument, index) => {
          const { contact, paymentInstrument, eligibility } = (currentPaymentInstrument || {})
          const isLastTwo = contactList?.length - index <= 2
          const isNextToAnimate =
            contactList?.length - index > 2 && contactList?.length - index <= 10
            const ineligibleBankAccountMessage = getIneligibleBankAccountMessage(t, paymentInstrument?.currency || '', eligibility)
          const secondaryText = getSecondaryText(currentPaymentInstrument, ineligibleBankAccountMessage)

          
          if (currentPaymentInstrument?.loading) {
            return (
              <SkeletonListItemAnimation key={paymentInstrument?.id} reference={paymentInstrument?.id}>
                <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
              </SkeletonListItemAnimation>
            )
          }
          return (
            <ListItemAnimation
              key={paymentInstrument?.id}
              reference={paymentInstrument?.id}
              index={index}
              listLength={contactList?.length}
              hasLoadedMore={hasLoadedMore}
              isLastTwo={isLastTwo}
              isNextToAnimate={isNextToAnimate}
            >
              <ActionListItem
                key={paymentInstrument?.id}
                id={paymentInstrument?.id}
                {...(!ineligibleBankAccountMessage && {onItemClick: () => handleContactSelect(contact, paymentInstrument)})}
                disabled={!!ineligibleBankAccountMessage}
                title={<Tooltip
                    bodyContent={contact?.name}
                    type="simple"
                  >
                    <p>{paymentInstrument?.description}</p>
                  </Tooltip>
                }
                testId={`contacts-step-list-item-${index}`}
                Avatar={<Avatar type="entity" name={paymentInstrument?.description ?? ''} {...(ineligibleBankAccountMessage && { colorType: 'disabled' })} />}
                BodyPre={paymentInstrument?.accountNumber}
                {...secondaryText}
              />
            </ListItemAnimation>
          )
        })}

        <Box flex justify="center" className="mt-2">
          {contactFilter?.length ? (
            <Button onClick={handleOnShowAll} variant="ghost">
            {t('contacts.showAll')}
          </Button>
            
          ) : (
            <Button
              onClick={handleOnLoadMore}
              variant="ghost"
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {hasNextPage ? t('contacts.loadMore') : t('contacts.noMoreBankAccounts')}
            </Button>
          )}
        </Box>
      </AnimatePresence>
    </motion.ul>
  )
}
